import React, { useState } from 'react';
import { BookOpen, Lightbulb, Search, ArrowRight, ExternalLink, CheckCircle, AlertTriangle, DollarSign, FileText, GraduationCap, PiggyBank, Target, Users } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';

// Financial Aid Glossary
const glossaryTerms = [
  { term: "COA (Cost of Attendance)", definition: "The total estimated cost to attend a college for one year, including tuition, fees, room, board, books, supplies, transportation, and personal expenses." },
  { term: "EFC / SAI (Student Aid Index)", definition: "The amount the federal government calculates your family can contribute to college costs. Previously called Expected Family Contribution (EFC), now Student Aid Index (SAI)." },
  { term: "FAFSA", definition: "Free Application for Federal Student Aid. The form you must complete to be considered for federal financial aid, including grants, loans, and work-study." },
  { term: "CSS Profile", definition: "A financial aid application required by many private colleges for institutional aid. More detailed than FAFSA and considers home equity and other assets." },
  { term: "Pell Grant", definition: "A federal grant for undergraduates with financial need. Maximum award is $7,395 (2024-25). Does not need to be repaid." },
  { term: "Merit Aid", definition: "Financial aid awarded based on academic achievement, talents, or other criteria—not financial need. Can come from colleges, states, or private organizations." },
  { term: "Need-Based Aid", definition: "Financial aid awarded based on demonstrated financial need, determined by your EFC/SAI and the cost of attendance." },
  { term: "Unmet Need / Gap", definition: "The difference between your financial need (COA minus EFC) and the aid you receive. Families must cover this gap with savings, loans, or additional income." },
  { term: "Direct Subsidized Loans", definition: "Federal loans for undergraduates with financial need. The government pays the interest while you're in school at least half-time." },
  { term: "Direct Unsubsidized Loans", definition: "Federal loans available regardless of financial need. Interest accrues while you're in school." },
  { term: "Parent PLUS Loans", definition: "Federal loans parents can borrow to cover remaining college costs. Credit check required. Higher interest rates than student loans." },
  { term: "Work-Study", definition: "A federal program that provides part-time jobs for students with financial need, allowing them to earn money for education expenses." },
  { term: "Net Price", definition: "The actual cost you pay after all grants and scholarships are subtracted from the Cost of Attendance. This is the true cost of college." },
  { term: "Sticker Price", definition: "The full published cost of attendance before any financial aid is applied. Often much higher than what students actually pay." },
  { term: "Financial Aid Package", definition: "The combination of grants, scholarships, loans, and work-study a college offers to help you pay for attendance." },
  { term: "Award Letter", definition: "A document from a college detailing the financial aid they're offering you, including grants, scholarships, loans, and work-study." },
  { term: "Professional Judgment", definition: "A process where financial aid officers can adjust your FAFSA information based on special circumstances (job loss, medical expenses, etc.)." },
  { term: "Verification", definition: "A process where your college confirms the accuracy of FAFSA data by requesting additional documentation like tax returns." },
  { term: "Outside Scholarship", definition: "Scholarships from sources other than your college, such as community organizations, employers, or private foundations." },
  { term: "Tuition Reciprocity", definition: "Agreements between states that allow residents to attend out-of-state public colleges at reduced tuition rates." },
  { term: "529 Plan", definition: "A tax-advantaged savings plan designed to encourage saving for future education costs. Earnings grow tax-free when used for qualified expenses." },
  { term: "Capitalized Interest", definition: "Unpaid interest that gets added to your loan principal balance. This increases the total amount you owe." },
  { term: "Grace Period", definition: "A period after graduation (typically 6 months) before you must start repaying student loans." },
  { term: "IDR (Income-Driven Repayment)", definition: "Federal loan repayment plans that cap payments at a percentage of your discretionary income. Includes SAVE, PAYE, IBR, and ICR plans." },
  { term: "PSLF", definition: "Public Service Loan Forgiveness. Forgives remaining federal loan balance after 120 qualifying payments while working for government or nonprofit employers." }
];

// Tips organized by category
const tipCategories = [
  {
    title: "Before You Apply",
    icon: Target,
    color: "text-purple-400",
    tips: [
      "File the FAFSA as early as possible after October 1st—some aid is first-come, first-served",
      "Create your FSA ID ahead of time (you and a parent each need one)",
      "Gather tax returns, bank statements, and other financial documents before starting",
      "Research each school's financial aid deadlines—they vary widely",
      "Use the Net Price Calculator on each college's website for estimates"
    ]
  },
  {
    title: "Maximizing Aid",
    icon: DollarSign,
    color: "text-emerald-400",
    tips: [
      "Apply to schools where your stats exceed their averages—you'll likely get merit aid",
      "Don't eliminate schools based on sticker price; private schools often have generous aid",
      "Ask about departmental scholarships in your major—they're often underutilized",
      "Check if your employer (or parents' employers) offer tuition benefits",
      "Look for scholarship opportunities from community organizations, religious groups, and local businesses"
    ]
  },
  {
    title: "Negotiating Your Package",
    icon: FileText,
    color: "text-cyan-400",
    tips: [
      "Always ask for more aid—the worst they can say is no",
      "Use competing offers from similar schools as leverage",
      "Explain any special circumstances (job loss, medical bills, supporting grandparents)",
      "Be polite and specific in your appeal letter; attach documentation",
      "Ask specifically which parts of your package can be adjusted"
    ]
  },
  {
    title: "Reducing Costs",
    icon: PiggyBank,
    color: "text-amber-400",
    tips: [
      "Consider starting at community college—save $20,000+ in two years",
      "Test out of classes with AP/CLEP exams to graduate early",
      "Buy used textbooks or rent; check the library first",
      "Become an RA—often comes with free room and board",
      "Look into tuition reciprocity agreements if you're near state borders"
    ]
  },
  {
    title: "Smart Borrowing",
    icon: GraduationCap,
    color: "text-pink-400",
    tips: [
      "Only borrow what you truly need—loans must be repaid with interest",
      "Max out federal loans before considering private loans",
      "Understand the difference between subsidized and unsubsidized loans",
      "Keep total debt under your expected first-year salary",
      "Make interest payments during school if possible to prevent capitalization"
    ]
  },
  {
    title: "After Graduation",
    icon: Users,
    color: "text-blue-400",
    tips: [
      "Sign up for autopay for a 0.25% interest rate reduction",
      "Consider income-driven repayment if your salary is low relative to debt",
      "Explore PSLF if you work in government or nonprofit sectors",
      "Refinance only after you've built strong credit and have stable income",
      "Put any windfalls (tax refunds, bonuses) toward your highest-interest debt"
    ]
  }
];

const Resources = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('tips');
  
  const filteredGlossary = glossaryTerms.filter(
    item => item.term.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.definition.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1.5s'}}></div>
      </div>

      <div className="container mx-auto px-6 py-12 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-cyan-400" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-3">Resources & Tips</h1>
            <p className="text-slate-400 max-w-xl mx-auto">
              Expert guidance to help you navigate college financing like a pro
            </p>
          </div>

          {/* Tab Switcher */}
          <div className="flex justify-center gap-4 mb-8">
            <button
              onClick={() => setActiveTab('tips')}
              className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
                activeTab === 'tips'
                  ? 'bg-emerald-500 text-white'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              <Lightbulb className="w-5 h-5" /> Smart Tips
            </button>
            <button
              onClick={() => setActiveTab('glossary')}
              className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
                activeTab === 'glossary'
                  ? 'bg-emerald-500 text-white'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              <BookOpen className="w-5 h-5" /> Glossary
            </button>
          </div>

          {/* Tips Tab */}
          {activeTab === 'tips' && (
            <div className="space-y-6 animate-in fade-in duration-300">
              {/* Quick Win Box */}
              <Card className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/5 border-emerald-500/20 p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">Quick Win: The One Thing That Matters Most</h3>
                    <p className="text-slate-300">
                      <strong className="text-emerald-400">File the FAFSA early.</strong> Every October 1st, the FAFSA opens. 
                      Some aid is first-come, first-served. Students who file within the first few weeks often receive 
                      more aid than those who wait. Set a calendar reminder now.
                    </p>
                  </div>
                </div>
              </Card>

              {/* Tip Categories */}
              <div className="grid md:grid-cols-2 gap-6">
                {tipCategories.map((category, index) => (
                  <Card key={index} className="bg-slate-900/50 border-slate-800 p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center">
                        <category.icon className={`w-5 h-5 ${category.color}`} />
                      </div>
                      <h2 className="text-lg font-bold text-white">{category.title}</h2>
                    </div>
                    <ul className="space-y-3">
                      {category.tips.map((tip, tipIndex) => (
                        <li key={tipIndex} className="flex items-start gap-3">
                          <ArrowRight className={`w-4 h-4 mt-1 flex-shrink-0 ${category.color}`} />
                          <span className="text-slate-300 text-sm">{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </Card>
                ))}
              </div>

              {/* Warning Box */}
              <Card className="bg-amber-500/10 border-amber-500/30 p-6">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-bold text-amber-400 mb-2">Common Mistakes to Avoid</h3>
                    <ul className="space-y-2 text-slate-300">
                      <li className="flex items-start gap-2">
                        <span className="text-amber-400">•</span>
                        <span>Missing deadlines—they're often different for each school</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-400">•</span>
                        <span>Assuming you won't qualify for aid based on income alone</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-400">•</span>
                        <span>Choosing private loans before maxing out federal options</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-400">•</span>
                        <span>Not negotiating your financial aid package</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-400">•</span>
                        <span>Borrowing more than you need "just in case"</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Glossary Tab */}
          {activeTab === 'glossary' && (
            <div className="space-y-6 animate-in fade-in duration-300">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Search terms..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-slate-900/50 border-slate-700 text-white pl-12 h-12"
                />
              </div>

              {/* Glossary Grid */}
              <div className="grid md:grid-cols-2 gap-4">
                {filteredGlossary.map((item, index) => (
                  <Card key={index} className="bg-slate-900/50 border-slate-800 p-5 hover:border-emerald-500/30 transition-colors">
                    <h3 className="text-emerald-400 font-bold mb-2">{item.term}</h3>
                    <p className="text-slate-300 text-sm leading-relaxed">{item.definition}</p>
                  </Card>
                ))}
              </div>

              {filteredGlossary.length === 0 && (
                <Card className="bg-slate-900/50 border-slate-800 p-12 text-center">
                  <p className="text-slate-400 text-lg">No terms found matching "{searchQuery}"</p>
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="text-emerald-400 hover:text-emerald-300 mt-2"
                  >
                    Clear search
                  </button>
                </Card>
              )}
            </div>
          )}

          {/* External Resources */}
          <Card className="bg-slate-900/50 border-slate-800 p-6 mt-8">
            <h2 className="text-xl font-bold text-white mb-4">Official Resources</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { name: "FAFSA Application", url: "https://studentaid.gov/h/apply-for-aid/fafsa", desc: "Apply for federal student aid" },
                { name: "Federal Student Aid", url: "https://studentaid.gov", desc: "Official government resource" },
                { name: "College Scorecard", url: "https://collegescorecard.ed.gov", desc: "Compare colleges with government data" },
                { name: "IRS Tax Benefits for Education", url: "https://www.irs.gov/credits-deductions/individuals/education-credits", desc: "Tax credits and deductions" }
              ].map((resource, index) => (
                <a
                  key={index}
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors group"
                >
                  <div>
                    <p className="text-white font-medium group-hover:text-emerald-400 transition-colors">{resource.name}</p>
                    <p className="text-slate-400 text-sm">{resource.desc}</p>
                  </div>
                  <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-emerald-400" />
                </a>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Resources;
